import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Trash2, Plus, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const EMPTY = { keywords: "", search_index: "", category: "", min_discount: 20 };

const Status = ({ configured }) => (
  <div className={`border p-5 flex gap-3 ${configured ? "border-[#00A651]" : "border-[#FF9900]"} bg-white`} data-testid="amazon-config-status">
    {configured ? <CheckCircle2 className="text-[#00A651] shrink-0" /> : <AlertTriangle className="text-[#FF9900] shrink-0" />}
    <div className="text-sm">
      <p className="font-semibold">{configured ? "Amazon Creators API collegata" : "Amazon Creators API non ancora collegata"}</p>
      {!configured && (
        <p className="text-[#555] mt-1">
          Aggiungi AMAZON_CREDENTIAL_ID, AMAZON_CREDENTIAL_SECRET e AMAZON_PARTNER_TAG (dal tuo account Amazon Associates) per attivare l'import automatico e l'aggiornamento prezzi giornaliero (ogni giorno alle 6:00).
        </p>
      )}
    </div>
  </div>
);

const SearchForm = ({ onAdd }) => {
  const [f, setF] = useState(EMPTY);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/deal-searches", { ...f, min_discount: +f.min_discount });
      setF(EMPTY);
      toast.success("Ricerca salvata");
      onAdd();
    } catch (er) { toast.error(errMsg(er)); }
  };
  const inp = (k, ph, extra = {}) => (
    <input data-testid={`search-form-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className="field" {...extra} />
  );
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-3">
      {inp("keywords", "Parole chiave (es. cuffie bluetooth)", { required: true })}
      {inp("search_index", "Indice Amazon (es. Electronics)")}
      {inp("category", "Categoria nel negozio", { required: true })}
      {inp("min_discount", "Sconto min %", { type: "number", min: 5, max: 90, required: true })}
      <button data-testid="search-form-submit" className="btn-primary h-full min-h-[44px] flex items-center justify-center gap-2 text-sm"><Plus size={16} /> Aggiungi</button>
    </form>
  );
};

export default function AdminAmazon() {
  const [status, setStatus] = useState(null);
  const [searches, setSearches] = useState([]);
  const load = () => {
    api.get("/admin/amazon/status").then((r) => setStatus(r.data));
    api.get("/admin/deal-searches").then((r) => setSearches(r.data));
  };
  useEffect(() => { load(); }, []);

  const sync = async () => {
    await api.post("/admin/amazon/sync");
    toast.success("Sincronizzazione avviata");
    setTimeout(load, 2500);
  };
  const del = async (id) => { await api.delete(`/admin/deal-searches/${id}`); load(); };

  if (!status) return <p className="tag">Caricamento...</p>;
  return (
    <div className="space-y-6" data-testid="admin-amazon">
      <div className="flex items-end justify-between">
        <div>
          <span className="tag text-[#0033FF]">Automazione</span>
          <h1 className="font-display font-black text-4xl tracking-tight">Amazon</h1>
        </div>
        <button data-testid="amazon-sync-button" onClick={sync} className="btn-ghost h-10 px-4 flex items-center gap-2 text-sm"><RefreshCw size={16} /> Sincronizza ora</button>
      </div>
      <Status configured={status.configured} />
      <div className={`border p-5 flex gap-3 bg-white ${status.zinc_configured ? "border-[#00A651]" : "border-[#FF9900]"}`} data-testid="zinc-config-status">
        {status.zinc_configured ? <CheckCircle2 className="text-[#00A651] shrink-0" /> : <AlertTriangle className="text-[#FF9900] shrink-0" />}
        <div className="text-sm">
          <p className="font-semibold">{status.zinc_configured ? "Ordini Amazon automatici attivi (Zinc)" : "Evasione ordini manuale: Zinc non collegato"}</p>
          {!status.zinc_configured && <p className="text-[#555] mt-1">Ogni ordine pagato ti arriva via email e compare come "Da ordinare su Amazon" nel pannello Ordini. Aggiungi ZINC_CLIENT_TOKEN per automatizzare.</p>}
        </div>
      </div>
      <p className="text-sm text-[#555]" data-testid="alert-email-info">
        Avvisi margine (prezzo Amazon +5% o sconto sotto il 20%) inviati a <b className="text-[#0F0F0F]">{status.alert_email || "—"}</b>
      </p>
      <section className="border border-[#0F0F0F] bg-white p-5 space-y-4">
        <span className="tag text-[#555]">Ricerche offerte automatiche</span>
        <SearchForm onAdd={load} />
        <div className="divide-y divide-[#0F0F0F]/15">
          {searches.length === 0 && <p className="text-sm text-[#555] py-2" data-testid="searches-empty">Nessuna ricerca configurata.</p>}
          {searches.map((s) => (
            <div key={s.id} className="py-2 flex items-center gap-4 text-sm" data-testid={`deal-search-${s.id}`}>
              <span className="flex-1 font-medium">{s.keywords}</span>
              <span className="font-mono text-xs text-[#555]">{s.search_index || "All"}</span>
              <span className="tag">{s.category}</span>
              <span className="font-mono text-[#FF3333]">≥ {s.min_discount}%</span>
              <button data-testid={`delete-search-${s.id}`} onClick={() => del(s.id)} className="p-1 hover:text-[#FF3333]"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </section>
      <section className="border border-[#0F0F0F] bg-white overflow-x-auto">
        <p className="tag p-4 border-b border-[#0F0F0F]">Ultime sincronizzazioni</p>
        <table className="w-full text-sm" data-testid="sync-runs-table">
          <tbody>
            {status.runs.length === 0 && <tr><td className="p-4 text-[#555]">Nessuna sincronizzazione ancora.</td></tr>}
            {status.runs.map((r, i) => (
              <tr key={i} className="border-t border-[#0F0F0F]/15">
                <td className="px-4 py-2 font-mono text-xs">{new Date(r.started_at).toLocaleString("it-IT")}</td>
                <td className="px-4 py-2 tag">{r.trigger}</td>
                <td className="px-4 py-2 tag">{r.status}</td>
                <td className="px-4 py-2 font-mono text-xs">agg. {r.updated} · disatt. {r.deactivated} · nuovi {r.imported} · avvisi {r.alerts || 0}</td>
                <td className="px-4 py-2 text-xs text-[#FF3333]">{r.errors?.join("; ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
